import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query. Returns `true` while the query matches.
 *
 * SSR-safe: starts as `false` when `window` is undefined, then syncs on mount.
 * Hot path: most callers only need a single breakpoint (e.g. `'(min-width:
 * 1024px)'`), so the hook only adds a `change` listener on the matched MQL.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
